import { Connector } from "@web3-react/types";
import {
  Connection,
  GetNetworkConnection,
  gnosisSafeConnection,
} from "./connectors";
import { useCallback, useEffect } from "react";
import { usePersistedSelectedWallet } from "./usePersistedSelectedWallet";
import { SupportedWallet } from "./wallets";
import { usePersistedProtocolInstance } from "./protocol-instance/usePersistedProtocolInstance";
import { isNotNullNorUndefined, mapNullable } from "@silo/utils";
import { memoize } from "lodash";
import {
  PROTOCOL_INSTANCE_CHAIN,
  ProtocolInstance,
  SupportedChainId,
} from "@silo/types";

export async function connect(
  connector: Connector,
  chainId?: SupportedChainId
) {
  try {
    if (connector.connectEagerly) {
      await connector.connectEagerly();
    } else {
      if (isNotNullNorUndefined(chainId)) {
        await connector.activate(chainId);
      } else {
        await connector.activate();
      }
    }
  } catch (error) {
    console.debug(`web3-react eager connection error: ${error}`);
  }
}

async function initialize(
  wallet: SupportedWallet | undefined,
  protocolInstance: ProtocolInstance | undefined,
  networkConnection: Connection
) {
  connect(gnosisSafeConnection.connector);
  if (wallet) {
    // await connecting to the persisted wallet - so that
    // it activates _before_ the fallback network connector
    await connect(wallet.connector);
  }

  const chainId = mapNullable(
    protocolInstance,
    (protocolInstance) => PROTOCOL_INSTANCE_CHAIN[protocolInstance]
  );
  connect(networkConnection.connector, chainId);
}

export const useEagerlyConnect = (
  getNetworkConnection: GetNetworkConnection
) => {
  const [maybePersistedSelectedWallet] = usePersistedSelectedWallet();
  const [persistedProtocolInstance] = usePersistedProtocolInstance();
  const networkConnection = getNetworkConnection();

  const initializeMemoized = useCallback(
    memoize(
      (
        maybePersistedSelectedWallet,
        persistedProtocolInstance,
        networkConnection
      ) => {
        initialize(
          maybePersistedSelectedWallet,
          persistedProtocolInstance,
          networkConnection
        );
      }
    ),
    [maybePersistedSelectedWallet, persistedProtocolInstance, networkConnection]
  );

  useEffect(() => {
    /**
     * Only run the initialization logic once unless `networkConnection` changes.
     * Connecting the connectors multiple times isn't really valid.
     * Safe to call from useEffect, since it's a noop after the first call.
     * and in strict mode - React calls useEffect twice.
     */
    initializeMemoized(
      maybePersistedSelectedWallet,
      persistedProtocolInstance,
      networkConnection
    );
  }, [initializeMemoized, networkConnection]);
};
